import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FieldManagementModal = ({ isOpen, onClose, onSave, existingFields = [] }) => {
  const [fields, setFields] = useState([]);
  const [newField, setNewField] = useState({
    id: '',
    label: '',
    type: 'text',
    required: false,
    category: 'details'
  });

  useEffect(() => {
    if (existingFields?.length > 0) {
      setFields(existingFields);
    }
  }, [existingFields]);

  const fieldTypes = [
    { value: 'text', label: 'نص' },
    { value: 'number', label: 'رقم' },
    { value: 'date', label: 'تاريخ' },
    { value: 'select', label: 'قائمة منسدلة' },
    { value: 'checkbox', label: 'خانة اختيار' },
    { value: 'textarea', label: 'نص طويل' }
  ];

  const categoryOptions = [
    { value: 'details', label: 'تفاصيل العقار' },
    { value: 'location', label: 'الموقع' },
    { value: 'owner', label: 'معلومات المالك' },
    { value: 'pricing', label: 'التسعير' },
    { value: 'contracts', label: 'العقود' },
    { value: 'reports', label: 'التقارير' },
    { value: 'other', label: 'أخرى' }
  ];

  const handleAddField = () => {
    if (!newField?.label?.trim()) {
      alert('الرجاء إدخال اسم الحقل');
      return;
    }

    const fieldId = `custom_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    const fieldToAdd = {
      ...newField,
      id: fieldId,
      isCustom: true
    };

    setFields([...fields, fieldToAdd]);
    setNewField({
      id: '',
      label: '',
      type: 'text',
      required: false,
      category: 'details'
    });
  };

  const handleRemoveField = (fieldId) => {
    setFields(fields?.filter(f => f?.id !== fieldId));
  };

  const handleSave = () => {
    onSave(fields);
    onClose();
  };

  if (!isOpen) return null;

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.15, ease: 'easeOut' }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.1, ease: 'easeIn' }
    }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.95,
      y: 20
    },
    visible: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { 
        duration: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.95,
      y: 20,
      transition: { 
        duration: 0.15,
        ease: 'easeIn'
      }
    }
  };

  const listItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.2,
        ease: 'easeOut'
      }
    })
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={overlayVariants}
        >
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div 
            className="relative bg-card rounded-lg shadow-warm border border-border w-full max-w-4xl max-h-[90vh] flex flex-col"
            variants={modalVariants}
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-heading font-semibold text-foreground flex items-center gap-2">
                <Icon name="Settings" size={24} color="var(--color-primary)" />
                إدارة الحقول المخصصة
              </h2>
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                iconSize={20}
                onClick={onClose}
              />
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <motion.div 
                className="bg-muted/30 p-4 rounded-lg border border-border"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.2 }}
              >
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Plus" size={18} color="var(--color-primary)" />
                  إضافة حقل جديد
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Input
                    label="اسم الحقل"
                    type="text"
                    placeholder="مثال: عدد المواقف"
                    value={newField?.label}
                    onChange={(e) => setNewField({ ...newField, label: e?.target?.value })}
                  />
                  <Select
                    label="نوع الحقل"
                    options={fieldTypes}
                    value={newField?.type}
                    onChange={(value) => setNewField({ ...newField, type: value })}
                  />
                  <Select
                    label="الفئة"
                    options={categoryOptions}
                    value={newField?.category}
                    onChange={(value) => setNewField({ ...newField, category: value })}
                  />
                </div>
                <div className="mt-4 flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newField?.required}
                      onChange={(e) => setNewField({ ...newField, required: e?.target?.checked })}
                      className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-ring"
                    />
                    <span className="text-sm text-foreground">حقل إلزامي</span>
                  </label>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Plus"
                    onClick={handleAddField}
                  >
                    إضافة الحقل
                  </Button>
                </div>
              </motion.div>

              <div>
                <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  <Icon name="List" size={18} color="var(--color-primary)" />
                  <span>الحقول المخصصة (<span className="font-data" dir="ltr">{fields?.length}</span>)</span>
                </h3>
                {fields?.length === 0 ? (
                  <motion.div 
                    className="text-center py-8 text-muted-foreground"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Icon name="Inbox" size={48} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">لا توجد حقول مخصصة بعد</p>
                  </motion.div>
                ) : (
                  <div className="space-y-2">
                    {fields?.map((field, index) => (
                      <motion.div
                        key={field?.id}
                        custom={index}
                        initial="hidden"
                        animate="visible"
                        variants={listItemVariants}
                        className="bg-background p-4 rounded-lg border border-border flex items-center justify-between hover:border-primary/50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <Icon name="GripVertical" size={18} color="var(--color-muted-foreground)" />
                            <div>
                              <p className="text-sm font-medium text-foreground">{field?.label}</p>
                              <div className="flex items-center gap-3 mt-1">
                                <span className="text-xs text-muted-foreground">
                                  النوع: {fieldTypes?.find(t => t?.value === field?.type)?.label}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  الفئة: {categoryOptions?.find(c => c?.value === field?.category)?.label}
                                </span>
                                {field?.required && (
                                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                    إلزامي
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          iconName="Trash2"
                          iconSize={18}
                          onClick={() => handleRemoveField(field?.id)}
                          className="text-error hover:text-error hover:bg-error/10"
                        />
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <motion.div 
              className="p-4 border-t border-border flex items-center justify-end gap-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.2 }}
            >
              <Button variant="outline" onClick={onClose}>
                إلغاء
              </Button>
              <Button variant="default" iconName="Save" onClick={handleSave}>
                حفظ التغييرات
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FieldManagementModal;